import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, type HTMLMotionProps } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * Badge variants for different states
 */
type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

/**
 * Badge sizes
 */
type BadgeSize = 'sm' | 'md' | 'lg';

/**
 * Props for the Badge component
 */
export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** The variant/status of the badge */
  variant?: BadgeVariant;
  /** Size of the badge */
  size?: BadgeSize;
  /** Badge content */
  children: ReactNode;
  /** Show a pulsing dot indicator */
  dot?: boolean;
  /** Icon to display before the text */
  icon?: ReactNode;
  /** Enable removal with animation */
  removable?: boolean;
  /** Callback when remove button is clicked */
  onRemove?: () => void;
  /** Animate appearance */
  animate?: boolean;
}

/**
 * Variant-specific styles
 */
/**
 * Rand und Punkt tragen die Farbe, die Flaeche bleibt frei.
 *
 * Vorher stand die Schrift in derselben Farbe wie ihre 20-%-Flaeche — bei
 * dieser Naehe erreichen sechs der zehn Kombinationen aus Variante und Theme
 * keine 4,5:1 (hell/success 3,83, dunkel/neutral 3,42, dunkel/error 3,71 …).
 * Eine schwaechere Flaeche rettet das nicht: Auch bei 10 % bleiben drei Faelle
 * darunter, weil der Ton selbst zu nah am Untergrund liegt.
 *
 * Ohne Fuellung reicht jede Variante von 4,81 bis 17,36 — das Farbsignal
 * bleibt in Rand und Punkt erhalten. `badgeKontrast.test.ts` rechnet es nach.
 */
const variantStyles: Record<BadgeVariant, { bg: string; border: string; text: string; dot: string }> = {
  default: {
    bg: '',
    border: 'border-surface-500/50',
    text: 'text-surface-700',
    dot: 'bg-surface-500',
  },
  success: {
    bg: '',
    border: 'border-success/50',
    text: 'text-success',
    dot: 'bg-success',
  },
  warning: {
    bg: '',
    border: 'border-warning/50',
    text: 'text-warning',
    dot: 'bg-warning',
  },
  error: {
    bg: '',
    border: 'border-error/50',
    text: 'text-error',
    dot: 'bg-error',
  },
  info: {
    bg: '',
    border: 'border-primary/50',
    text: 'text-primary',
    dot: 'bg-primary',
  },
  neutral: {
    bg: '',
    border: 'border-surface-500/50',
    text: 'text-surface-700',
    dot: 'bg-surface-500',
  },
};

/**
 * Size-specific styles
 */
const sizeStyles: Record<BadgeSize, { badge: string; dot: string; icon: string }> = {
  sm: {
    badge: 'px-2 py-0.5 text-xs gap-1',
    dot: 'w-1.5 h-1.5',
    icon: 'w-3 h-3',
  },
  md: {
    badge: 'px-2.5 py-1 text-sm gap-1.5',
    dot: 'w-2 h-2',
    icon: 'w-4 h-4',
  },
  lg: {
    badge: 'px-3 py-1.5 text-base gap-2',
    dot: 'w-2.5 h-2.5',
    icon: 'w-5 h-5',
  },
};

/**
 * Animation variants for the badge
 */
const badgeVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.8, transition: { duration: 0.15 } },
};

/**
 * Badge component with Liquid Glass design
 *
 * @example
 * ```tsx
 * <Badge variant="success">Active</Badge>
 *
 * <Badge variant="warning" dot>
 *   Pending
 * </Badge>
 *
 * <Badge variant="error" removable onRemove={() => {}}>
 *   Failed
 * </Badge>
 * ```
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      variant = 'neutral',
      size = 'md',
      children,
      dot = false,
      icon,
      removable = false,
      onRemove,
      animate = true,
      className,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const styles = variantStyles[variant];
    const sizes = sizeStyles[size];

    const BadgeContent = (
      <>
        {/* Dot indicator */}
        {dot && (
          <span className="relative flex-shrink-0">
            <span className={cn(sizes.dot, 'rounded-full', styles.dot)} />
            <span
              className={cn(
                sizes.dot,
                'rounded-full absolute inset-0 animate-ping opacity-75',
                styles.dot
              )}
            />
          </span>
        )}

        {/* Icon */}
        {icon && !dot && (
          <span className={cn(sizes.icon, 'flex-shrink-0', styles.text)}>{icon}</span>
        )}

        {/* Text content */}
        <span>{children}</span>

        {/* Remove button */}
        {removable && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.();
            }}
            className={cn(
              'ml-0.5 -mr-1 p-0.5 rounded-full',
              'hover:bg-white/10 transition-colors duration-150',
              'focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30'
            )}
            aria-label={t('a11y.remove')}
          >
            <svg
              className={cn(sizes.icon, 'flex-shrink-0')}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </>
    );

    if (animate) {
      return (
        <AnimatePresence mode="wait">
          <motion.span
            ref={ref}
            variants={badgeVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            className={cn(
              // Base styles
              'inline-flex items-center font-medium',
              'rounded-full',
              // Glass effect
              'backdrop-blur-md',
              styles.bg,
              styles.border,
              styles.text,
              'border',
              // Size
              sizes.badge,
              className
            )}
            {...(props as Omit<HTMLMotionProps<'span'>, 'ref'>)}
          >
            {BadgeContent}
          </motion.span>
        </AnimatePresence>
      );
    }

    return (
      <span
        ref={ref}
        className={cn(
          // Base styles
          'inline-flex items-center font-medium',
          'rounded-full',
          // Glass effect
          'backdrop-blur-md',
          styles.bg,
          styles.border,
          styles.text,
          'border',
          // Size
          sizes.badge,
          className
        )}
        {...props}
      >
        {BadgeContent}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

export default Badge;
